import BlogCard from "@/components/Ui/Pages/BlogCard/BlogCard";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { t } from "@/helper/helper";
import { baseURL } from "@/services/api";
import { IBlog, IBlogGeneric } from "global-types/global.types";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";

interface Props {
  data: IBlog<IBlogGeneric[]>;
  locale: string;
}

export default function Blog({ data, locale }: Props) {
  const { asPath } = useRouter();

  return (
    <>
      <NextSeo
        title={`${t("blog", locale)} | ${SITE_NAME}`}
        // @ts-ignore
        description={t("blog", locale)}
        canonical={SITE_URL + "/" + locale + asPath}
      />
      <Wrapper>
        <div className="blog">
          <div className="breadcrumb">
            <Link href={`/${locale}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`/${locale}/blog`}>{t("blog", locale)}</Link>
          </div>
          <h1>{t("blog", locale)}</h1>
          <div className="blog-content">
            {data.data.map(({ slug, thumbnail, name, created_at }, index) => (
              <BlogCard
                key={index.toString() + name}
                locale={locale}
                thumbnail={thumbnail}
                name={name}
                slug={slug}
                createdAt={created_at}
              />
            ))}
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;

  const blogData = await fetch(baseURL + "/blog", {
    headers: {
      "x-localization": locale ? locale : "az",
    },
  });

  return {
    props: {
      data: await blogData.json(),
      locale,
    },
    revalidate: 60,
  };
};

const Wrapper = styled.div`
  .blog {
    width: 1289px;
    margin: 0 auto;
    padding-bottom: 7rem;

    h1 {
      font-size: 36px;
      text-align: center;
      margin: 50px 0;
    }

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .blog-content {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      column-gap: 16px;
      row-gap: 36px;
      margin-top: 3.6rem;
    }

    @media screen and (max-width: 549.9px) {
      .blog-content {
        grid-template-columns: repeat(1, 1fr);
      }
    }
  }

  .blog-card {
    background: #1b1c1f;
    width: 100%;
    height: 470px;
    box-sizing: border-box;
    border-radius: 6px;
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
    }

    .blog-card-title {
      position: absolute;
      bottom: 60px;
      left: 24px;
      background: #1b1c1f;
      padding: 12px;
      font-style: normal;
      font-weight: normal;
      font-size: 18px;
      line-height: 150%;
      color: #ffffff;
      width: 70%;
    }

    .blog-card-bottom {
      position: absolute;
      bottom: 24px;
      left: 24px;
      background: #333333;
      padding: 0 20px;
      height: 36px;
      display: flex;
      align-items: center;

      span {
        font-style: normal;
        font-weight: 300;
        font-size: 12px;
        line-height: 100%;
        color: #fdfdfd;
      }

      p {
        display: flex;
        align-items: center;
        font-style: normal;
        font-weight: 300;
        font-size: 12px;
        line-height: 100%;
        color: #fdfdfd;
        margin-left: 24px;

        img {
          margin-left: 4px !important;
          width: 14px !important;
          height: 10px !important;
          object-fit: contain;
        }
      }
    }

    @media screen and (max-width: 991.9px) {
      height: 400px;
    }

    @media screen and (max-width: 767.9px) {
      height: 360px;

      .blog-card-title {
        font-size: 1.6rem;
      }
    }

    @media screen and (max-width: 549.9px) {
      height: 300px;

      .blog-card-title {
        font-size: 14px;
        font-size: 1.6rem;
        padding: 1.2rem;
        left: 16px;
        right: 16px;
        width: initial;
      }

      .blog-card-bottom {
        left: 16px;
      }
    }
  }
`;
